import axios from 'axios';
import {UserPlanTier} from '../utils/plan';

export interface AuthUserEnterpriseSummary {
  organizationId: string;
  organizationName: string;
  role: 'owner' | 'admin' | 'coach' | 'recruiter' | 'staff';
  canRecruit: boolean;
  canManageSeats: boolean;
}

export interface AuthUser {
  id: string;
  email: string;
  name: string;
  avatar?: string;
  planTier: UserPlanTier;
  enterprise?: AuthUserEnterpriseSummary | null;
}

interface AuthResponse {
  success: boolean;
  token: string;
  user: AuthUser;
}

export interface FacebookAuthSession {
  state: string;
  status: 'started' | 'ready' | 'completed' | 'failed';
  linked: boolean;
  requiresEmail: boolean;
  requiresEmailVerification: boolean;
  email: string;
  profile: {
    facebookId?: string;
    displayName?: string;
    profilePictureUrl?: string;
  };
}

export interface InstagramAuthSession {
  state: string;
  status: 'started' | 'pending_email' | 'ready' | 'completed' | 'failed';
  linked: boolean;
  requiresEmail: boolean;
  requiresEmailVerification: boolean;
  email: string;
  profile: {
    instagramId?: string;
    username?: string;
    displayName?: string;
    accountType?: string;
    profilePictureUrl?: string;
  };
}

export interface TwitterAuthSession {
  state: string;
  status: 'started' | 'pending_email' | 'ready' | 'completed' | 'failed';
  linked: boolean;
  requiresEmail: boolean;
  requiresEmailVerification: boolean;
  email: string;
  profile: {
    twitterId?: string;
    username?: string;
    displayName?: string;
    profileImageUrl?: string;
  };
}

export interface LinkedInAuthSession {
  state: string;
  status: 'started' | 'pending_email' | 'ready' | 'completed' | 'failed';
  linked: boolean;
  requiresEmail: boolean;
  requiresEmailVerification: boolean;
  email: string;
  profile: {
    linkedinId?: string;
    displayName?: string;
    profileImageUrl?: string;
  };
}

export interface TikTokAuthSession {
  state: string;
  status: 'started' | 'pending_email' | 'ready' | 'completed' | 'failed';
  linked: boolean;
  requiresEmail: boolean;
  requiresEmailVerification: boolean;
  email: string;
  profile: {
    tiktokId?: string;
    displayName?: string;
    profileImageUrl?: string;
  };
}

interface InstagramAuthStartResponse {
  success: boolean;
  authorizeUrl: string;
}

interface TwitterAuthStartResponse {
  success: boolean;
  authorizeUrl: string;
}

interface LinkedInAuthStartResponse {
  success: boolean;
  authorizeUrl: string;
}

interface TikTokAuthStartResponse {
  success: boolean;
  authorizeUrl: string;
}

interface FacebookAuthStartResponse {
  success: boolean;
  authorizeUrl: string;
}

interface FacebookAuthSessionResponse {
  success: boolean;
  session: FacebookAuthSession;
}

interface InstagramAuthSessionResponse {
  success: boolean;
  session: InstagramAuthSession;
}

interface TwitterAuthSessionResponse {
  success: boolean;
  session: TwitterAuthSession;
}

interface LinkedInAuthSessionResponse {
  success: boolean;
  session: LinkedInAuthSession;
}

interface TikTokAuthSessionResponse {
  success: boolean;
  session: TikTokAuthSession;
}

const API_BASE_URL = (process.env.REACT_APP_API_URL || '/api').replace(/\/$/, '');

const authClient = axios.create({
  baseURL: API_BASE_URL,
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json',
  },
});

const getErrorMessage = (error: unknown) => {
  if (axios.isAxiosError(error)) {
    return (
      (error.response?.data as {message?: string; error?: {message?: string}} | undefined)?.message ||
      (error.response?.data as {message?: string; error?: {message?: string}} | undefined)?.error?.message ||
      error.message
    );
  }

  if (error instanceof Error) {
    return error.message;
  }

  return 'Something went wrong';
};

export const authApi = {
  async login(email: string, password: string) {
    const response = await authClient.post<AuthResponse>('/auth/login', {email, password});
    return response.data;
  },

  async register(name: string, email: string, password: string) {
    const response = await authClient.post<AuthResponse>('/auth/register', {name, email, password});
    return response.data;
  },

  async loginWithGoogle(idToken: string) {
    const response = await authClient.post<AuthResponse>('/auth/google', {idToken});
    return response.data;
  },

  async loginWithFacebook(accessToken: string) {
    const response = await authClient.post<AuthResponse>('/auth/facebook', {accessToken});
    return response.data;
  },

  async startFacebookLogin(returnPath: string) {
    const response = await authClient.post<FacebookAuthStartResponse>('/auth/facebook/start', {returnPath});
    return response.data;
  },

  async getFacebookLoginSession(state: string) {
    const response = await authClient.get<FacebookAuthSessionResponse>(
      `/auth/facebook/session?state=${encodeURIComponent(state)}`
    );
    return response.data.session;
  },

  async completeFacebookLogin(payload: {state: string}) {
    const response = await authClient.post<AuthResponse>('/auth/facebook/complete', payload);
    return response.data;
  },

  async startTwitterLogin(returnPath: string) {
    const response = await authClient.post<TwitterAuthStartResponse>('/auth/twitter/start', {returnPath});
    return response.data;
  },

  async getTwitterLoginSession(state: string) {
    const response = await authClient.get<TwitterAuthSessionResponse>(
      `/auth/twitter/session?state=${encodeURIComponent(state)}`
    );
    return response.data.session;
  },

  async sendTwitterLoginEmailCode(state: string, email: string) {
    const response = await authClient.post<{success: boolean; message: string}>('/auth/twitter/email', {
      state,
      email,
    });
    return response.data;
  },

  async completeTwitterLogin(payload: {state: string; email?: string; code?: string}) {
    const response = await authClient.post<AuthResponse>('/auth/twitter/complete', payload);
    return response.data;
  },

  async startLinkedInLogin(returnPath: string) {
    const response = await authClient.post<LinkedInAuthStartResponse>('/auth/linkedin/start', {returnPath});
    return response.data;
  },

  async getLinkedInLoginSession(state: string) {
    const response = await authClient.get<LinkedInAuthSessionResponse>(
      `/auth/linkedin/session?state=${encodeURIComponent(state)}`
    );
    return response.data.session;
  },

  async sendLinkedInLoginEmailCode(state: string, email: string) {
    const response = await authClient.post<{success: boolean; message: string}>('/auth/linkedin/email', {
      state,
      email,
    });
    return response.data;
  },

  async completeLinkedInLogin(payload: {state: string; email?: string; code?: string}) {
    const response = await authClient.post<AuthResponse>('/auth/linkedin/complete', payload);
    return response.data;
  },

  async startTikTokLogin(returnPath: string) {
    const response = await authClient.post<TikTokAuthStartResponse>('/auth/tiktok/start', {returnPath});
    return response.data;
  },

  async getTikTokLoginSession(state: string) {
    const response = await authClient.get<TikTokAuthSessionResponse>(
      `/auth/tiktok/session?state=${encodeURIComponent(state)}`
    );
    return response.data.session;
  },

  async sendTikTokLoginEmailCode(state: string, email: string) {
    const response = await authClient.post<{success: boolean; message: string}>('/auth/tiktok/email', {
      state,
      email,
    });
    return response.data;
  },

  async completeTikTokLogin(payload: {state: string; email?: string; code?: string}) {
    const response = await authClient.post<AuthResponse>('/auth/tiktok/complete', payload);
    return response.data;
  },

  async startInstagramLogin(returnPath: string) {
    const response = await authClient.post<InstagramAuthStartResponse>('/auth/instagram/start', {returnPath});
    return response.data;
  },

  async getInstagramLoginSession(state: string) {
    const response = await authClient.get<InstagramAuthSessionResponse>(
      `/auth/instagram/session?state=${encodeURIComponent(state)}`
    );
    return response.data.session;
  },

  async sendInstagramLoginEmailCode(state: string, email: string) {
    const response = await authClient.post<{success: boolean; message: string}>('/auth/instagram/email', {
      state,
      email,
    });
    return response.data;
  },

  async completeInstagramLogin(payload: {state: string; email?: string; code?: string}) {
    const response = await authClient.post<AuthResponse>('/auth/instagram/complete', payload);
    return response.data;
  },

  async getCurrentUser(token: string) {
    const response = await authClient.get<{success: boolean; user: AuthUser}>('/auth/me', {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    return response.data.user;
  },

  getErrorMessage,
};

export {API_BASE_URL};
